import React from 'react';
import { ArrowLeft, MapPin, Car, Bus, Plane, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

const ComoLlegar = () => {
  const navigate = useNavigate();
  const lang = useLanguage();

  const texts = {
    es: {
      backTo: 'Volver a Contacto',
      title: 'Cómo Llegar',
      subtitle: 'Clínica Estética Alameda de Marbella',
      clinicName: 'Clínica Estética Alameda',
      location: 'Marbella',
      addressTitle: 'Dirección',
      phonesTitle: 'Teléfonos',
      appointments: 'Citas',
      howToGetTitle: 'Cómo llegar',
      byCar: 'En coche',
      byCarDesc: 'Fácil acceso desde la A-7. Parking público disponible en las inmediaciones.',
      byBus: 'En autobús',
      byBusDesc: 'Paradas de autobús urbano a pocos minutos andando.',
      fromAirport: 'Desde el aeropuerto',
      fromAirportDesc: '45 min desde el Aeropuerto de Málaga (AGP). Servicio de recogida disponible.',
      viewMap: 'Ver en Google Maps',
      callForAppt: 'Llamar para cita',
      openInMaps: 'Abrir en Google Maps',
      firstVisit: '¿Primera visita?',
      firstVisitDesc: 'Solicite una consulta de valoración personalizada.',
      requestAppt: 'Solicitar cita: 952 76 45 47',
    },
    en: {
      backTo: 'Back to Contact',
      title: 'How to Get Here',
      subtitle: 'Alameda Aesthetic Clinic of Marbella',
      clinicName: 'Alameda Aesthetic Clinic',
      location: 'Marbella',
      addressTitle: 'Address',
      phonesTitle: 'Phones',
      appointments: 'Appointments',
      howToGetTitle: 'How to get here',
      byCar: 'By car',
      byCarDesc: 'Easy access from the A-7. Public parking available nearby.',
      byBus: 'By bus',
      byBusDesc: 'Urban bus stops within walking distance.',
      fromAirport: 'From the airport',
      fromAirportDesc: '45 min from Málaga Airport (AGP). Pickup service available.',
      viewMap: 'View on Google Maps',
      callForAppt: 'Call for appointment',
      openInMaps: 'Open in Google Maps',
      firstVisit: 'First visit?',
      firstVisitDesc: 'Request a personalized assessment consultation.',
      requestAppt: 'Request appointment: 952 76 45 47',
    },
    fr: {
      backTo: 'Retour au Contact',
      title: 'Comment Venir',
      subtitle: 'Clinique Esthétique Alameda de Marbella',
      clinicName: 'Clinique Esthétique Alameda',
      location: 'Marbella',
      addressTitle: 'Adresse',
      phonesTitle: 'Téléphones',
      appointments: 'Rendez-vous',
      howToGetTitle: 'Comment venir',
      byCar: 'En voiture',
      byCarDesc: 'Accès facile depuis l\'A-7. Parking public disponible à proximité.',
      byBus: 'En bus',
      byBusDesc: 'Arrêts de bus urbain à quelques minutes à pied.',
      fromAirport: 'Depuis l\'aéroport',
      fromAirportDesc: '45 min de l\'aéroport de Málaga (AGP). Service de transport disponible.',
      viewMap: 'Voir sur Google Maps',
      callForAppt: 'Appeler pour rendez-vous',
      openInMaps: 'Ouvrir dans Google Maps',
      firstVisit: 'Première visite?',
      firstVisitDesc: 'Demandez une consultation d\'évaluation personnalisée.',
      requestAppt: 'Demander rendez-vous: 952 76 45 47',
    },
    pt: {
      backTo: 'Voltar para Contato',
      title: 'Como Chegar',
      subtitle: 'Clínica Estética Alameda de Marbella',
      clinicName: 'Clínica Estética Alameda',
      location: 'Marbella',
      addressTitle: 'Endereço',
      phonesTitle: 'Telefones',
      appointments: 'Agendamentos',
      howToGetTitle: 'Como chegar',
      byCar: 'De carro',
      byCarDesc: 'Acesso fácil pela A-7. Estacionamento público disponível nas proximidades.',
      byBus: 'De ônibus',
      byBusDesc: 'Pontos de ônibus urbano a poucos minutos a pé.',
      fromAirport: 'Do aeroporto',
      fromAirportDesc: '45 min do Aeroporto de Málaga (AGP). Serviço de transporte disponível.',
      viewMap: 'Ver no Google Maps',
      callForAppt: 'Ligar para agendar',
      openInMaps: 'Abrir no Google Maps',
      firstVisit: 'Primeira visita?',
      firstVisitDesc: 'Solicite uma consulta de avaliação personalizada.',
      requestAppt: 'Solicitar consulta: 952 76 45 47',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion/contacto')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.backTo}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          
          {/* Marbella */}
          <div className="bg-ivory p-8 rounded-none shadow-lg mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gold/10 rounded-none flex items-center justify-center">
                <MapPin className="text-gold" size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-light text-[#1C1C1A]">{t.clinicName}</h2>
                <p className="text-text-secondary">{t.location}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-[#1C1C1A] mb-4">{t.addressTitle}</h3>
                <p className="text-text-secondary mb-4">
                  C/ Carlos Mackintosh, 3<br />
                  29602 Marbella<br />
                  Málaga, España
                </p>
                
                <h3 className="font-semibold text-[#1C1C1A] mb-4 mt-6">{t.phonesTitle}</h3>
                <p className="text-text-secondary">
                  <strong>{t.appointments}:</strong> 952 76 45 47<br />
                  <strong>WhatsApp:</strong> 696 345 792
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-[#1C1C1A] mb-4">{t.howToGetTitle}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Car className="text-gold mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-medium text-[#1C1C1A]">{t.byCar}</h4>
                      <p className="text-text-secondary text-sm">
                        {t.byCarDesc}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Bus className="text-gold mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-medium text-[#1C1C1A]">{t.byBus}</h4>
                      <p className="text-text-secondary text-sm">
                        {t.byBusDesc}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Plane className="text-gold mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-medium text-[#1C1C1A]">{t.fromAirport}</h4>
                      <p className="text-text-secondary text-sm">
                        {t.fromAirportDesc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => window.open('https://maps.google.com/?q=Calle+Carlos+Mackintosh+3+Marbella+29602', '_blank')}
                className="bg-gold hover:bg-gold text-white"
              >
                <MapPin className="mr-2" size={18} />
                {t.viewMap}
              </Button>
              <Button 
                onClick={() => window.open('tel:952764547', '_self')}
                variant="outline"
                className="border-gold text-gold hover:bg-gold/5"
              >
                <Phone className="mr-2" size={18} />
                {t.callForAppt}
              </Button>
            </div>
          </div>

          {/* Map embed placeholder */}
          <div className="bg-gray-100 rounded-none h-96 flex items-center justify-center mb-8">
            <div className="text-center">
              <MapPin className="text-gray-400 mx-auto mb-4" size={48} />
              <p className="text-text-muted">C/ Carlos Mackintosh, 3</p>
              <p className="text-text-muted">29602 Marbella, Málaga</p>
              <Button 
                onClick={() => window.open('https://maps.google.com/?q=Calle+Carlos+Mackintosh+3+Marbella+29602', '_blank')}
                className="mt-4 bg-gold hover:bg-gold text-white"
              >
                {t.openInMaps}
              </Button>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-[#1C1C1A] to-[#1C1C1A] p-8 rounded-none text-white text-center">
            <h3 className="text-2xl font-light mb-4">{t.firstVisit}</h3>
            <p className="text-white/80 mb-6">
              {t.firstVisitDesc}
            </p>
            <Button 
              onClick={() => window.open('tel:952764547', '_self')}
              className="bg-gold hover:bg-gold text-white px-8 py-3"
            >
              {t.requestAppt}
            </Button>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ComoLlegar;
